import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";
import PostCard from "./PostCard";
import "./peopleFeed.css";
import "./orgActivityFeed.css";

export default function OrgActivityFeed({
    organization_id,
    post_author,
    showSearch = true,
    externalFilterBar = false,
    postTypeFilter,
    onPostTypeFilterChange,
    onPostTypesLoaded,
}) {
    const { auth } = useContext(AuthContext);

    const [posts, setPosts] = useState([]);
    const [userLikes, setUserLikes] = useState([]);
    const [postTypesList, setPostTypesList] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const [internalPostTypeFilter, setInternalPostTypeFilter] = useState(undefined);
    const postTypeFilterSelect = externalFilterBar ? postTypeFilter : internalPostTypeFilter;
    const setPostTypeFilterSelect = externalFilterBar ? onPostTypeFilterChange : setInternalPostTypeFilter;
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const limit = 40;

    const offsetRef = useRef(0);
    const isFetchingRef = useRef(false);

    const fetchPosts = useCallback(async (isReset = false) => {
        if (isFetchingRef.current || (!organization_id && !post_author)) return;
        isFetchingRef.current = true;
        setIsFetching(true);

        const currentOffset = isReset ? 0 : offsetRef.current;

        try {
            const response = await API.getPosts(currentOffset, limit, 'recent', postTypeFilterSelect, searchQuery, organization_id, post_author);
            const newPosts = response.data;

            if (isReset) {
                setPosts(newPosts);
            } else {
                setPosts(prev => [...prev, ...newPosts.filter(p => !prev.find(ep => ep.post_id === p.post_id))]);
            }
            offsetRef.current = currentOffset + newPosts.length;
            setHasMore(!searchQuery && newPosts.length === limit);
        } catch (error) {
            console.error("Error fetching activity:", error);
        } finally {
            isFetchingRef.current = false;
            setIsFetching(false);
            setHasFetchedOnce(true);
        }
    }, [organization_id, post_author, postTypeFilterSelect, searchQuery]);

    const refreshPosts = useCallback(() => fetchPosts(true), [fetchPosts]);

    useEffect(() => {
        fetchPosts(true);
    }, [fetchPosts]);

    const handleSearchSubmit = () => {
        setSearchQuery(searchInput.trim());
    };

    const handleResetFilters = () => {
        setPostTypeFilterSelect(undefined);
        setSearchInput("");
        setSearchQuery("");
    };

    useEffect(() => {
        API.getAllPostTypes()
            .then(data => {
                setPostTypesList(data.data);
                if (onPostTypesLoaded) onPostTypesLoaded(data.data);
            })
            .catch(error => console.log(error));

        if (!auth.user) return;
        API.getUserLikes(auth.user.user_id)
            .then(data => setUserLikes(data.data))
            .catch(error => console.log(error));
    }, [auth.user, onPostTypesLoaded]);

    if (isFetching && posts.length === 0) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="org-people-feed mt-sm-0 mt-0 pt-0">
            {!externalFilterBar && (
                <div className="people-filter-bar">
                    {postTypesList.length > 0 && (
                        <select
                            className="form-select people-type-filter-select"
                            value={postTypeFilterSelect !== undefined ? postTypeFilterSelect : ""}
                            onChange={(e) => setPostTypeFilterSelect(e.target.value)}
                        >
                            <option value="" disabled>Post Type</option>
                            {postTypesList.map(type => (
                                <option key={type.posttype_id} value={type.posttype_id}>{type.post_type}</option>
                            ))}
                        </select>
                    )}

                    {showSearch && (
                        <div className="input-group activity-search-group">
                            <input
                                className="form-control activity-search-input"
                                type="text"
                                placeholder="Search title, author, org/division, or body"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                            />
                            <button
                                className="btn activity-search-submit"
                                type="button"
                                onClick={handleSearchSubmit}
                                aria-label="Search"
                            >
                                <i className="bi bi-search" />
                            </button>
                        </div>
                    )}

                    <button
                        className="clear-people-filter-button"
                        onClick={handleResetFilters}
                    >Reset</button>
                </div>
            )}

            {hasFetchedOnce && posts.length === 0 ? (
                <p className="org-prof-head-text">No activity yet.</p>
            ) : (
                <div className="org-activity-list">
                    {posts.map(post => (
                        <div key={post.post_id} className="mb-0">
                            <PostCard
                                compact
                                post_id={post.post_id}
                                title={post.post_title}
                                author={post.ProfileInfo ? `${post.ProfileInfo.first_name} ${post.ProfileInfo.last_name}` : "Anonymous"}
                                author_id={post.post_author}
                                post_type={post.post_type}
                                postTypeName={postTypesList.find(t => t.posttype_id === post.post_type)?.post_type ?? ''}
                                userLikes={userLikes}
                                body={post.post_body}
                                org_id={post.organization_id}
                                div_id={post.division_id}
                                postDate={post.created_at}
                                likes={post.likes_count}
                                reads={post.reads}
                                comments={post.PostComments}
                                user_id={post.ProfileInfo?.user_id}
                                event_location={post.event_location}
                                event_date={post.event_date}
                                event_start={post.event_start}
                                event_end={post.event_end}
                                event_type={post.event_type}
                                refresh={refreshPosts}
                            />
                        </div>
                    ))}

                    {hasMore && posts.length > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => fetchPosts(false)}
                                disabled={isFetching}
                            >
                                {isFetching ? "Loading..." : "Load more"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
